import { memo, useContext, useEffect, useState } from 'react'
import { notifyError } from '../../../../utils/notifications'
import { useParams } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { Flex, Row, Typography } from 'antd'
import { decrypt } from '../../../../utils/encryption'
import { NavContext } from '../../../../context/global_context'
import Button from '../../../common/Button'
import { getRequest } from '../../../../services/base'
import {
  usePageContext,
  useMappedContext,
  SET_FUNCTIONAL_DIAGRAM_DETAILS,
} from './context/context'
import SearchSection from './SearchSection'
import CandidateConnectorsSection from './CandidateConnectorsSection'
import MappedConnectorsSection from './MappedConnectorsSection'
import FunctionalDiagramForm from '../form'
import SelectedSalesCodes from './SelectedSalesCodes'
import FunctionalDiagramPreview from './sales_code_preview'

const FunctionalDiagramEdit = () => {
  const intl = useIntl()
  const { functional, vehicle } = useParams()
  const [, navDispatch] = useContext(NavContext)
  const [state, dispatch] = usePageContext()
  const [mappedState] = useMappedContext()
  const [showFunctionalModal, setShowFunctionalModal] = useState(false)

  useEffect(() => {
    getFunctionalDiagramDetails()
  }, [])

  const getFunctionalDiagramDetails = async () => {
    navDispatch({
      type: 'loader',
      data: { caption: intl.formatMessage({ id: 'functionalDiagram.fetchingDetails' }), showLoader: true },
      notification: {},
    })

    let res = await getRequest(`/api/sub_system/${decrypt(functional)}/?make=${vehicle}`)
    if (res?.status === false) {
      navDispatch({ type: 'loader', data: {}, notification: {} })
      notifyError(navDispatch, intl.formatMessage({ id: 'functionalDiagram.detailsLoadError' }))
      return
    }
    getFunctionalDiagramConnectors(res)
  }

  const getFunctionalDiagramConnectors = async (functional_diagram_details) => {
    let res = await getRequest(
      `/api/sub_system/functional-diagram-connector/${decrypt(functional)}/?make=${vehicle}`
    )
    if (res?.status === false) {
      navDispatch({ type: 'loader', data: {}, notification: {} })
      notifyError(navDispatch, intl.formatMessage({ id: 'functionalDiagram.detailsLoadError' }))
      return
    }
    dispatch({
      type: SET_FUNCTIONAL_DIAGRAM_DETAILS,
      data: { ...functional_diagram_details, sales_code_data: res.sales_code },
      mappedConnector: res.data,
      connectorCategories: res.category,
    })

    navDispatch({
      type: 'header',
      data: {
        header: intl.formatMessage({ id: 'functionalDiagram.adminTools' }),
        sub_header: `${intl.formatMessage({ id: 'functionalDiagram.breadcrumbPrefix' })} / ${vehicle} / ${intl.formatMessage({ id: 'layout.functionalDiagram', defaultMessage: 'Functional Diagram' })} / ${functional_diagram_details?.sub_system_id} - ${functional_diagram_details?.name}`,
        id: '',
        show_back_btn: true,
        dtc_details: {},
        back_btn_url: `/vehicles/${vehicle}/functional-diagram`,
        full_width_container: true,
      },
    })
  }

  return (
    <div className="page functional-diagram-edit-mapping">
      <div className="body">
        {showFunctionalModal && (
          <FunctionalDiagramForm
            showModal={showFunctionalModal}
            setShowModal={setShowFunctionalModal}
            getFunctionalDiagramList={() => {}}
            selectedFunctionalDiagram={state.functionalDiagramDetails}
            onSave={() => {
              setShowFunctionalModal(false)
              getFunctionalDiagramConnectors(state.functionalDiagramDetails)
            }}
            onClose={() => setShowFunctionalModal(false)}
          />
        )}

        <Row className="title" gutter={[0, 8]}>
          <Flex justify="space-between" align="center" className="full-width">
            <Flex align="center" gap={0}>
              <Typography.Title level={4} style={{ margin: 0 }}>
                {state.functionalDiagramDetails?.sub_system_id}
              </Typography.Title>
              <span aria-hidden="true">&nbsp;&mdash;&nbsp;</span>
              <Typography.Text>{state.functionalDiagramDetails?.name}</Typography.Text>
            </Flex>
            <Flex gap={16}>
              <Button onClick={() => setShowFunctionalModal(true)} testId="fd-add-sales-code">
                {intl.formatMessage({ id: 'functionalDiagram.addSalesCode' })}
              </Button>
              <FunctionalDiagramPreview
                vehicle={vehicle}
                functionalDiagramId={state.functionalDiagramDetails?.id}
                hasMappedConnectors={mappedState.mappedConnectors?.length > 0}
              />
            </Flex>
          </Flex>
          <Flex justify="space-between" align="center" className="full-width">
              <Typography.Text strong style={{whiteSpace: 'nowrap'}}>
                {intl.formatMessage({ id: 'functionalDiagram.selectedSalesCodes' })}
                {` (${state.functionalDiagramDetails?.sales_code_data?.length || 0})`}
              </Typography.Text>
              <span aria-hidden="true">&nbsp;&mdash;&nbsp;</span>
              <SelectedSalesCodes salesCodes={state.functionalDiagramDetails?.sales_code_data} />
          </Flex>
        </Row>

        <Flex className="col-container">
          <div className="col search-col" data-testid="fd-search-col">
            <SearchSection />
          </div>
          <div className="col candidate-col" data-testid="fd-candidate-col">
            <CandidateConnectorsSection />
          </div>
          <div className="col mapped-col" data-testid="fd-mapped-col">
            <MappedConnectorsSection />
          </div>
        </Flex>
      </div>
    </div>
  )
}

export default memo(FunctionalDiagramEdit)
